import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './PosterPage.css';

function PosterPage() {
  const [eventDetails, setEventDetails] = useState({
    eventName: 'Sample Event',
    eventLocation: 'Sample Location',
    eventStartTime: '6:00 PM',
    eventDetails: 'Join us for an amazing event with fun activities and prizes!',
    schedule: [],
  });

  const [savedPosters, setSavedPosters] = useState([]); // State to store saved posters
  const [selectedTemplate, setSelectedTemplate] = useState('/images/poster-template.jpg'); // State for selected template
  const [selectedFont, setSelectedFont] = useState("Poppins, sans-serif"); // State for selected font (default to Poppins)
  const [scheduleFontSize, setScheduleFontSize] = useState(0.9); // Initial font size for the schedule
  const scheduleRef = useRef(null); // Ref to track the schedule element

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  useEffect(() => {
    const adjustFontSize = () => {
      const scheduleElement = scheduleRef.current;
      if (scheduleElement) {
        const canvasHeight = document.getElementById('poster-canvas').offsetHeight;
        let scheduleHeight = scheduleElement.offsetHeight;
  
        // Aggressively reduce the font size if the schedule overflows
        while (scheduleHeight > canvasHeight * 0.4 && scheduleFontSize > 0.05) {
          setScheduleFontSize((prevFontSize) => prevFontSize - 0.05); // Reduce font size by 0.05rem
          scheduleHeight = scheduleElement.offsetHeight; // Recalculate height after shrinking
        }
      }
    };
  
    adjustFontSize();
  }, [eventDetails.schedule, scheduleFontSize]); // Re-run whenever the schedule or font size changes
  
  const handleTemplateChange = (e) => {
    setSelectedTemplate(e.target.value); // Update the selected template
  };

  const handleAddSubfield = () => {
    setEventDetails((prevDetails) => ({
      ...prevDetails,
      schedule: [...prevDetails.schedule, { time: '', prize: '' }],
    }));
  };

  const handleSubfieldChange = (index, field, value) => {
    const updatedSchedule = [...eventDetails.schedule];
    updatedSchedule[index][field] = value;
    setEventDetails((prevDetails) => ({
      ...prevDetails,
      schedule: updatedSchedule,
    }));
  };

  const renderEventDetails = () => {
    return eventDetails.eventDetails.split('\n').map((line, index) => {
      if (line.startsWith('*')) {
        // Render as a bullet point
        return (
          <li key={index} style={{ listStyleType: 'disc', marginLeft: '20px' }}>
            {line.substring(1).trim()}
          </li>
        );
      }
      // Render as a normal paragraph
      return (
        <p key={index} style={{ margin: '5px 0' }}>
          {line}
        </p>
      );
    });
  };

  const handleSavePoster = async () => {
    const posterElement = document.getElementById('poster-canvas');
    if (!posterElement) return;

    try {
      // Capture the poster as a canvas
      const canvas = await html2canvas(posterElement, {
        scale: 2,
        useCORS: true,
      });

      // Convert the canvas to an image
      const image = canvas.toDataURL('image/jpeg', 1.0);

      // Create a PDF with US Letter dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [8.5, 11],
      });

      // Add the image to the PDF
      pdf.addImage(image, 'JPEG', 0, 0, 8.5, 11);

      // Save the poster to the state
      const posterBlob = pdf.output('blob');
      const posterUrl = URL.createObjectURL(posterBlob);
      setSavedPosters((prevPosters) => [
        ...prevPosters,
        { id: Date.now(), name: eventDetails.eventName, url: posterUrl },
      ]);

      alert('Poster saved successfully!');
    } catch (error) {
      console.error('Error saving poster:', error);
      alert('Failed to save poster.');
    }
  };

  const handleDeleteSubfield = (index) => {
    setEventDetails((prevDetails) => ({
      ...prevDetails,
      schedule: prevDetails.schedule.filter((_, i) => i !== index), // Remove the subfield at the specified index
    }));
  };

  const handleDeletePoster = (id) => {
    setSavedPosters((prevPosters) => prevPosters.filter((poster) => poster.id !== id));
  };

  return (
    <div className="poster-page">
      <h1>Generate Event Poster</h1>

      <div className="poster-container">
        {/* Form Section */}
        <div className="poster-form">
          <label className="form-label">
            Event Name:
            <input
              type="text"
              name="eventName"
              value={eventDetails.eventName}
              onChange={handleInputChange}
              placeholder="Enter event name"
            />
          </label>
          <label className="form-label">
            Event Location:
            <input
              type="text"
              name="eventLocation"
              value={eventDetails.eventLocation}
              onChange={handleInputChange}
              placeholder="Enter event location"
            />
          </label>
          <label className="form-label">
            Event Start Time:
            <input
              type="text"
              name="eventStartTime"
              value={eventDetails.eventStartTime}
              onChange={handleInputChange}
              placeholder="Enter event start time"
            />
          </label>
          <label className="form-label">
            Event Details:
            <textarea
              name="eventDetails"
              value={eventDetails.eventDetails}
              onChange={handleInputChange}
              placeholder="Enter event details"
            />
          </label>

          {/* Template Selection */}
          <label className="select-template">
            Select Template:
            <select value={selectedTemplate} onChange={handleTemplateChange}>
              <option value="/images/poster-template.jpg">Template 1</option>
              <option value="/images/poster-template2.jpg">Template 2</option>
              <option value="/images/poster-template3.jpg">Template 3</option>
              <option value="/images/poster-template4.jpg">Template 4</option>
              <option value="/images/poster-template5.jpg">Template 5</option>
            </select>
          </label>

                  <label className="select-font">
                    Select Font:
                    <select value={selectedFont} onChange={(e) => setSelectedFont(e.target.value)}>
                      <option value="Poppins, sans-serif">Poppins</option>
                      <option value="'Oswald', sans-serif">Oswald</option>
                      <option value="'Montserrat', sans-serif">Montserrat</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="'Courier New', monospace">Courier New</option>
                    </select>
                  </label>

          {/* Schedule Subfields */}
          <div className="schedule-section">
            <h3>Schedule</h3>
            {eventDetails.schedule.map((subfield, index) => (
              <div key={index} className="schedule-subfield">
                <label>
                  Time:
                  <input
                    type="text"
                    value={subfield.time}
                    onChange={(e) =>
                      handleSubfieldChange(index, 'time', e.target.value)
                    }
                    placeholder="Enter time"
                  />
                </label>
                <label>
                  Prize:
                  <input
                    type="text"
                    value={subfield.prize}
                    onChange={(e) =>
                      handleSubfieldChange(index, 'prize', e.target.value)
                    }
                    placeholder="Enter prize"
                  />
                </label>
                <button
    type="button"
    className="delete-subfield-button"
    onClick={() => handleDeleteSubfield(index)} // Call the delete function
  >
    Delete
  </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddSubfield}
              className="add-subfield-button"
            >
              Add Subfield
            </button>
          </div>

          <button onClick={handleSavePoster} className="add-subfield-button">
            Save Poster
          </button>
        </div>

        {/* Poster Preview Section */}
        <div id="poster-canvas" className="poster-canvas">
          <img
            src={selectedTemplate} // Dynamically update the template
            alt="Poster Template"
            className="poster-background"
          />
         <div
  className="poster-content"
  style={{ fontFamily: selectedFont }} // Dynamically apply the selected font
>
            <h2 className="poster-title">{eventDetails.eventName}</h2>
            <p className="poster-location">{eventDetails.eventLocation}</p>
            <p className="poster-time">{eventDetails.eventStartTime}</p>
            <div className="poster-details">
  <ul>{renderEventDetails()}</ul>
</div>
            <h3 className="poster-schedule-header">Schedule</h3>
            <table className="poster-schedule-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Prize</th>
                </tr>
              </thead>
              <tbody>
                {eventDetails.schedule.map((subfield, index) => (
                  <tr key={index}>
                    <td>{subfield.time}</td>
                    <td>{subfield.prize}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <img
              src="/images/kmlogo.png"
              alt="KM Logo"
              className="poster-logo"
            />
          </div>
        </div>
      </div>

      {/* Saved Posters Section */}
      <div className="saved-posters">
        <h2 className="saved-poster">My Saved Posters</h2>
        {savedPosters.length === 0 ? (
          <p className='saved'>No saved posters yet.</p>
        ) : (
          <ul>
            {savedPosters.map((poster) => (
              <li key={poster.id}>
                <a href={poster.url} target="_blank" rel="noopener noreferrer">
                  {poster.name}
                </a>
                <button onClick={() => handleDeletePoster(poster.id)}>Delete</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default PosterPage;